/**
 * Language Storage Management
 *
 * Handles cookies, localStorage, and browser language detection
 */

import { systemLogger } from '../logging';
import {
  type SupportedLanguage,
  isValidLanguage,
  LANGUAGE_COOKIE_NAME,
  LANGUAGE_STORAGE_KEY,
  COOKIE_EXPIRY_DAYS
} from './constants';

/**
 * Get language from cookie
 */
export function getLanguageFromCookie(): SupportedLanguage | null {
  try {
    const cookies = document.cookie.split(';');
    const languageCookie = cookies.find(cookie =>
      cookie.trim().startsWith(`${LANGUAGE_COOKIE_NAME}=`)
    );

    if (languageCookie) {
      const cookieParts = languageCookie.split('=');
      if (cookieParts.length > 1 && cookieParts[1]) {
        const language = cookieParts[1].trim();
        return isValidLanguage(language) ? language : null;
      }
    }

    return null;
  } catch (error) {
    systemLogger.warn('Language detection failed', error);
    return null;
  }
}

/**
 * Set language cookie
 */
export function setLanguageCookie(language: SupportedLanguage): void {
  try {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + COOKIE_EXPIRY_DAYS);

    const cookieValue = `${LANGUAGE_COOKIE_NAME}=${language}; expires=${expiry.toUTCString()}; path=/; SameSite=Lax`;
    document.cookie = cookieValue;
  } catch (error) {
    systemLogger.error('Language service operation failed', error);
  }
}

/**
 * Get language from localStorage
 */
export function getLanguageFromStorage(): SupportedLanguage | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored && isValidLanguage(stored) ? stored : null;
  } catch (error) {
    systemLogger.warn('Language detection failed', error);
    return null;
  }
}

/**
 * Set language in localStorage
 */
export function setLanguageInStorage(language: SupportedLanguage): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    systemLogger.error('Language service operation failed', error);
  }
}

/**
 * Get browser language
 */
export function getBrowserLanguage(): SupportedLanguage | null {
  try {
    const languageParts = navigator.language.split('-');
    if (languageParts.length > 0 && languageParts[0]) {
      const browserLang = languageParts[0]; // Get base language (e.g., 'en' from 'en-US')
      return isValidLanguage(browserLang) ? browserLang : null;
    }
    return null;
  } catch (error) {
    systemLogger.warn('Language detection failed', error);
    return null;
  }
}

/**
 * Get URL language parameter
 */
export function getLanguageFromURL(): SupportedLanguage | null {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lng');
    return urlLang && isValidLanguage(urlLang) ? urlLang : null;
  } catch (error) {
    systemLogger.warn('Language detection failed', error);
    return null;
  }
}

/**
 * Sync language to storage
 */
export function syncLanguageToStorage(language: SupportedLanguage): void {
  setLanguageCookie(language);
  setLanguageInStorage(language);
}